import React, { useMemo, useState } from 'react';
import GenericDrawer from '@components/generic-drawer/GenericDrawer';
import { toast } from 'react-toastify';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { useMutation } from '@apollo/client';
import { getError } from '@utils/graph-utils';
import { useParams } from 'react-router-dom';
import { modifyAmendment, recalculateCancellationAmendment } from './queries';
import { getCancellationInputs } from './content';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import DateService from '@services/DateService';
import { IAbstractRecord } from '@models';

interface ICancellationDetailsDrawerProps {
  open: boolean;
  recalculate: boolean;
  onClose: () => void;
  onSuccess: () => void;
  data: IAbstractRecord;
  lovs: Record<string, Record<string, string>>;
  access: string;
}

const CancellationDetailsDrawer: React.FC<ICancellationDetailsDrawerProps> = ({
  open,
  recalculate,
  onClose,
  onSuccess,
  data,
  lovs,
  access,
}) => {
  const params = useParams();
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const [saveCancellationData] = useMutation(
    recalculate ? recalculateCancellationAmendment() : modifyAmendment()
  );

  const inputForm = useMemo(
    () => getCancellationInputs(lovs, data, access),
    [lovs, data]
  );

  const submitForm = async (formValues: Record<string, any>) => {
    const [formData] = normalizeDynamicFormDrawerValues(inputForm, formValues);
    setFormDisabled(true);
    setSubmitButtonState('loading');
    try {
      const { errors } = await saveCancellationData({
        variables: {
          entityId: params.id,
          cancellationIssuanceDate: DateService.formatDateBackend(
            formData.cancellationIssuanceDate
          ),
          cancellationEffectiveDate: DateService.formatDateBackend(
            formData.cancellationEffectiveDate
          ),
          cancellationReason: formData.cancellationReason,
        },
      });

      if (!errors || errors.length === 0) {
        toast.success(
          <ToastSuccessMessage>
            Cancellation details updated successfully!
          </ToastSuccessMessage>
        );
        setSubmitButtonState('success');
        onSuccess();
        onClose();
      } else {
        setSubmitButtonState(undefined);
        const errorMessage = getError({ errors });
        toast.error(<ToastErrorMessage>{errorMessage}</ToastErrorMessage>);
      }
    } catch (err) {
      const errorMessage = getError({ err });
      toast.error(<ToastErrorMessage>{errorMessage}</ToastErrorMessage>);
      setSubmitButtonState(undefined);
    } finally {
      setFormDisabled(false);
    }
  };

  return (
    <DynamicFormDrawer
      isOpen={open}
      title="Modify Cancellation Details"
      sections={inputForm}
      onSubmit={(vals) => submitForm(vals)}
      buttonText="Submit"
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
      onClose={onClose}
    />
  );
};

export default CancellationDetailsDrawer;
